<template>
  <div
    class="folder-list-container select-folder-modal-content"
    :class="{ 'is-dragging-files': isDraggingFiles }"
    @dragover.prevent="onFilesDragOver"
    @dragleave="onFilesDragLeave"
    @drop.prevent="onBodyDrop"
  >
    <AllModals />
    <b-modal
      id="rename-folder-modal"
      title="Rename Folder"
      centered
      @ok="renameFolder"
      @cancel="cancelRenameFolder"
      @hidden="cancelRenameFolder"
    >
      <b-form-group label="Folder name">
        <b-form-input
          v-model="renamingFolderName"
          placeholder="Enter folder name"
          @keyup.enter="renameFolder"
        />
      </b-form-group>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="outline-secondary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!renamingFolderName.trim()"
          @click="ok()"
        >
          Rename
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="select-folder-modal-preview"
      title="Preview"
      size="xl"
      centered
      body-class="select-folder-preview__body"
      hide-footer
    >
      <img
        v-if="previewItem && isPreviewImage"
        class="select-folder-preview__img"
        :src="previewItem.path"
        alt=""
      >
      <b-alert
        v-else
        show
        variant="warning"
      >
        Preview not available
      </b-alert>
    </b-modal>

    <div class="folder-sticky-header">
      <SelectFolderModalToolbar
        v-model="searchQuery"
        band
        placeholder="Search…"
        :show-create-folder="isFilesTab && !rootFolderId"
        :show-favorites="isFilesTab && !rootFolderId"
        :favorites-active="favoritesOnly"
        @search="performSearch"
        @clear="clearSearch"
        @create-folder="createFolder"
        @toggle-favorites="toggleFavorites"
      >
        <template #breadcrumb>
          <FolderBreadcrumb
            v-if="isFilesTab"
            :breadcrumbs="breadcrumbs"
            :home-title="rootFolderName || 'Home'"
            @goTo="goToFolder"
          />
          <!-- On the toolbar row: the dialog is a fixed 760px, so its own line costs a row of tiles. -->
          <span
            v-if="isSearchActive && wasEnterPressed && !isListLoading"
            class="folder-search-note"
          >
            {{ totalCount }} result{{ totalCount === 1 ? '' : 's' }} for "{{ searchQuery }}"
          </span>
        </template>
      </SelectFolderModalToolbar>
    </div>

    <div
      v-if="items.length || isListLoading || creatingFolder"
      class="folder-scroll-area"
    >
      <template v-if="isListLoading || creatingFolder">
        <div
          class="folders-grid"
          :style="gridVars"
        >
          <div
            v-for="n in pageSize"
            :key="n"
            class="w-100 h-100"
            style="min-height: 167px;"
          >
            <b-skeleton
              width="100%"
              height="88%"
            />
            <b-skeleton />
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-if="folderItems.length || otherItems.length"
          class="folders-grid"
          :style="gridVars"
        >
          <div
            v-if="folderItems.length"
            class="folder-items picker-grid-flow"
          >
            <GridItem
              v-for="item in folderItems"
              :key="item.id"
              :item="item"
              :is-selected="isSelected(item)"
              :show-edit-button="canRenameFolder(item)"
              :upload-on-drop="Boolean(item.id)"
              :draggable="Boolean(item.created_at)"
              @click="onSelectedItem(item)"
              @dblclick="dblHandler(item)"
              @edit="startRenamingFolder(item)"
              @dragstart="onItemDragStart($event, item)"
              @drop="onFolderDrop($event, item)"
            />
          </div>

          <div
            v-if="otherItems.length"
            class="picker-grid-flow"
          >
            <div
              v-for="otherItem in otherItems"
              :key="otherItem.id"
              :ref="`hoverWrap-${otherItem.id}`"
              class="select-folder-hover-wrap"
              @mouseenter="onHoverEnter(otherItem)"
              @mouseleave="hoveredId = null"
            >
              <GridItem
                :item="otherItem"
                :is-selected="isSelected(otherItem)"
                :is-available-overlay="availableOverlay"
                draggable="true"
                @click="onSelectedItem(otherItem)"
                @dblclick="fileDoubleClickHandler(otherItem)"
                @dragstart="onItemDragStart($event, otherItem)"
              />

              <div
                v-if="hoveredId === otherItem.id"
                class="select-folder-hover-actions"
                :style="getHoverActionsStyle(otherItem.id)"
              >
                <button
                  type="button"
                  class="select-folder-action-btn select-folder-action-btn--download"
                  title="Download"
                  @click.stop.prevent="downloadItem(otherItem)"
                >
                  <i class="fa fa-download" />
                </button>
                <button
                  v-if="isPreviewableImage(otherItem)"
                  type="button"
                  class="select-folder-action-btn select-folder-action-btn--preview"
                  title="Preview"
                  @click.stop.prevent="openPreview(otherItem)"
                >
                  <i class="fa fa-search-plus" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div
      v-else
      class="folder-scroll-area"
    >
      <b-alert
        show
        variant="warning"
      >
        Folder is empty
      </b-alert>
    </div>

    <div
      v-if="isFilesTab"
      class="folder-drop-hint"
    >
      <UploadIcon
        :width="16"
        :height="16"
      />
      <span>{{ dropHint }}</span>
    </div>

    <div
      v-if="totalCount > effectivePageSize"
      class="folder-sticky-footer"
    >
      <div class="d-flex justify-content-center align-items-center item-pagination">
        <ItemPagination
          label-only
          :value="currentPage"
          :per-page="effectivePageSize"
          :total-rows="totalCount"
          :disabled="isListLoading"
          @change="onChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ItemPagination from "@frontend/components/modules/file-browser/browser/ItemPagination.vue";
/* eslint vue/enforce-style-attribute: 0 */
import { createNamespacedHelpers } from "vuex";
import GridItem from '../browser/GridItem.vue';
import { PRODUCT_PICKER_COLUMNS, PRODUCT_PICKER_PAGE_SIZE } from '@frontend/constants/select-image-grid';
import { TABS, VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE } from "../constants";
import { FILE_TYPE } from "../../../../constants/file-image-types";
import { fetchListDataBasedActionCreator } from "../dto/fetch-list-dto";
import FileBrowser from "../../../../services/api/file-browser";
import SelectFolderModalToolbar from "./SelectFolderModalToolbar.vue";
import FolderBreadcrumb from "@frontend/components/common/atoms/FolderBreadcrumb.vue";
import { Upload as UploadIcon } from "lucide-vue";
import { downloadFileLink, isBriefLinkedFolder, isPreviewableImage } from "../helpers";
import AllModals from "./AllModals.vue";
import { MODALS } from "../constants";
import { createModal } from "./action-creater";

const DEFAULT_FOLDER_NAME = 'New Folder';
const NO_UPLOAD_TARGET_MESSAGE = 'Open a folder first, or drop the files onto a folder.';
const NOT_FILES_TAB_MESSAGE = 'Uploading is only available on the Files tab.';

const { mapGetters: mapGettersFileBrowser, mapActions: mapActionsFileBrowser, mapMutations: mapMutationsFileBrowser } =
  createNamespacedHelpers('fileBrowser');
const { mapGetters: mapGettersFileUploadImages } = createNamespacedHelpers('fileUploadImages');

export default {
  name: 'SelectFolderModalContentView',
  components: {
    ItemPagination, AllModals, SelectFolderModalToolbar, FolderBreadcrumb, GridItem, UploadIcon },
  props: {
    pageSize: { type: Number, default: PRODUCT_PICKER_PAGE_SIZE },
    allowMultipleSelection: { type: Boolean, default: true },
    isLightWeight: { type: Boolean, default: false },
    // Root the view at one folder (the picker's "AI Studio" tab): it opens there,
    // Home goes back there, and the folder above it is never shown. (POR-14659)
    rootFolderId: { type: Number, default: null },
    rootFolderName: { type: String, default: '' },
  },
  data() {
    return {
      currentPage: 1,
      rootPage: 1,
      selectedItems: [],
      breadcrumbs: [],
      currentFolderId: this.rootFolderId,
      searchQuery: '',
      wasEnterPressed: false,
      creatingFolder: false,
      renamingFolderId: null,
      renamingFolderName: '',
      renamingFolderItem: null,
      hoveredId: null,
      previewItem: null,
      hoverBounds: {},
      isDraggingFiles: false,
      dragLeaveTimer: null,
      isDestroyed: false,
      favoritesOnly: false,
    };
  },
  computed: {
    // Predates ItemGrid, so it borrows the Select Image numbers rather than the component itself.
    gridVars() {
      return {
        '--c-item-grid-columns': PRODUCT_PICKER_COLUMNS,
        '--c-item-grid-columns-md': PRODUCT_PICKER_COLUMNS,
      };
    },
    ...mapGettersFileBrowser(['items', 'totalCount', 'folderCount', 'isListLoading', 'modal']),
    ...mapGettersFileUploadImages(['getTab']),
    availableOverlay() {
      // In this modal there may be no file-browser route context; rely on store tab.
      return [TABS.PRODUCTS, TABS.STOCK_IMAGES, TABS.FILES].includes(this.getTab);
    },
    isRootedFolder() {
      return Boolean(this.rootFolderId) && this.currentFolderId === this.rootFolderId;
    },
    showsBackTile() {
      return Boolean(this.currentFolderId) && !this.isRootedFolder;
    },
    // The back tile repeats on every page, so it spends a slot; the backend reserves the same one.
    effectivePageSize() {
      return Math.max(1, this.pageSize - (this.showsBackTile ? 1 : 0));
    },
    folderItems() {
      const folders = this.items.filter(item => item.type === FILE_TYPE.FOLDER);
      // At the rooted folder, drop the "..." item that would climb above it.
      return this.isRootedFolder
        ? folders.filter(item => !Object.prototype.hasOwnProperty.call(item, 'is_root'))
        : folders;
    },
    otherItems() {
      return this.items.filter(item => item.type !== FILE_TYPE.FOLDER);
    },
    isSearchActive() {
      return !!this.searchQuery.trim();
    },
    isPreviewImage() {
      return IMAGE_FILE_TYPES.includes(this.previewItem?.type);
    },
    isSelected() {
      const selectedIds = this.selectedItems.map(({ id }) => id);
      return item => selectedIds.includes(item.id);
    },
    // Files-only: Products lives in `files` / `file_folders`, which these endpoints never touch.
    isFilesTab() {
      return this.getTab === TABS.FILES;
    },
    dropHint() {
      return this.currentFolderId
        ? 'Drag and drop files here to upload'
        : 'Drop files onto a folder to upload into it';
    },
  },
  watch: {
    getTab() {
      this.favoritesOnly = false;
      this.resetToFirstPage();
      this.fetchFolderList();
    },
    // deleteById (store/file-browser/index.js) can't know which folder/page this
    // embedded view is showing, so off-route it skips its own auto-refresh and
    // relies on this instead: when the Delete modal closes, refresh using our own
    // already-correct currentFolderId/currentPage/searchQuery.
    // `items`/`modal` are single, app-wide fileBrowser state, not scoped per
    // SelectFolderModal instance — several can be mounted at once (e.g. brief
    // pickers) — so this only acts when the deleted item was actually in our own
    // list, to avoid refreshing (and overwriting the shared list for) an unrelated
    // picker elsewhere on the page. Bulk delete isn't offered inside this embedded
    // view (its "Delete Selected" toolbar only renders in the standalone
    // MainView.vue page), so MODALS.DELETE_SELECTED is not handled here.
    modal(newModal, oldModal) {
      const wasOwnDeletedItem = oldModal?.type === MODALS.DELETE_ITEM
        && this.items.some(({ id }) => id === oldModal.payload?.id);

      if (wasOwnDeletedItem && newModal?.type !== oldModal?.type) {
        this.setSelectedItems();
        this.refreshAfterDelete();
      }
    },
  },
  mounted() {
    this.fetchFolderList();
  },
  beforeDestroy() {
    this.isDestroyed = true;
    clearTimeout(this.dragLeaveTimer);
  },
  methods: {
    isPreviewableImage,
    ...mapActionsFileBrowser(['fetchList', 'editFile', 'uploadFilesToFolder', 'patchPreparedUploads', 'updatePreparedUploadPagination']),
    ...mapMutationsFileBrowser({
      setModal: 'SET_MODAL',
    }),
    onHoverEnter(item) {
      this.hoveredId = item.id;
      this.$nextTick(() => {
        this.updateHoverBounds(item.id);
        requestAnimationFrame(() => this.updateHoverBounds(item.id));
      });
    },
    updateHoverBounds(id) {
      const ref = this.$refs[`hoverWrap-${id}`];
      const wrap = Array.isArray(ref) ? ref[0] : ref;
      if (!wrap) return;
      const preview = wrap.querySelector('.c-grid-view-item__preview');
      if (!preview) return;

      const wrapRect = wrap.getBoundingClientRect();
      const previewRect = preview.getBoundingClientRect();

      this.$set(this.hoverBounds, id, {
        left: previewRect.left - wrapRect.left,
        top: previewRect.top - wrapRect.top,
        width: previewRect.width,
        height: previewRect.height,
      });
    },
    getHoverActionsStyle(id) {
      const bounds = this.hoverBounds[id];
      if (!bounds) {
        return {
          top: '0',
          left: '0',
          right: '0',
          bottom: '44px',
        };
      }
      return {
        left: `${bounds.left}px`,
        top: `${bounds.top}px`,
        width: `${bounds.width}px`,
        height: `${bounds.height}px`,
      };
    },
    setSelectedItems(selectedItems = []) {
      this.selectedItems = selectedItems;
      this.$emit('onSelectedItems', selectedItems);
    },
    currentListDto(searchKey = '') {
      const dto = fetchListDataBasedActionCreator(
        this.getTab,
        this.currentFolderId ? this.currentFolderId.toString() : null,
        this.currentPage,
        searchKey,
      );

      dto.query.favorites = this.favoritesOnly;
      dto.query.isLightWeight = this.isLightWeight || null;
      dto.query.pageSize = this.pageSize;
      dto.query.paginateFolders = true;
      dto.query.hideBackTile = this.isRootedFolder || null;

      // The AI Studio folder lists the newest images first.
      if (this.rootFolderId) {
        dto.query.sort_field = 'id';
        dto.query.sort = 'desc';
      }

      return dto;
    },
    fetchFolderList() {
      return this.fetchList(this.currentListDto());
    },
    refreshList() {
      return this.isSearchActive ? this.fetchSearchResults() : this.fetchFolderList();
    },
    // Deleting the last item on a page beyond page 1 leaves `currentPage` pointing
    // past the new last page — the backend doesn't clamp it, it just returns an
    // empty list for that offset. Step back a page and retry rather than showing
    // "Folder is empty" while earlier pages still have items.
    async refreshAfterDelete() {
      await this.refreshList();
      // The back tile sits on every page, so "empty" means nothing but that tile.
      const realItems = this.items.filter(({ name }) => name !== '...');
      if (!realItems.length && this.currentPage > 1) {
        this.currentPage -= 1;
        await this.refreshList();
      }
    },
    toggleFavorites() {
      this.favoritesOnly = !this.favoritesOnly;
      this.resetToFirstPage();

      return this.refreshList();
    },
    performSearch() {
      if (this.searchQuery.trim()) {
        this.resetToFirstPage();
        this.wasEnterPressed = true;
        this.fetchSearchResults();
      } else {
        this.clearSearch();
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.wasEnterPressed = false;
      this.fetchFolderList();
    },
    fetchSearchResults() {
      return this.fetchList(this.currentListDto(this.searchQuery));
    },
    // A filter change rebuilds the root listing, so the remembered page stops meaning anything.
    resetToFirstPage() {
      this.currentPage = 1;
      this.rootPage = 1;
    },
    setFolderPage(nextFolderId) {
      if (!this.currentFolderId) {
        this.rootPage = this.currentPage;
      }

      this.currentPage = nextFolderId ? 1 : this.rootPage;
    },
    async dblHandler(item) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      this.setFolderPage(item.id);
      this.setSelectedItems();
      this.currentFolderId = item.id;

      if (item.is_root === true) {
        this.breadcrumbs = [];
      }

      if (item.is_root === false) {
        this.breadcrumbs.pop();
      }

      if (item.created_at) {
        this.breadcrumbs.push({ id: item.id, text: item.name });
      }

      this.searchQuery = currentSearchQuery;
      await this.refreshList();
    },
    async goToFolder(folderId, breadcrumbIndex) {
      const currentSearchQuery = this.searchQuery;
      this.wasEnterPressed = false;
      breadcrumbIndex = folderId?.index;
      folderId = folderId?.id;
      if (folderId == null && this.rootFolderId) folderId = this.rootFolderId;
      if (this.currentFolderId === folderId) return;

      this.setFolderPage(folderId);
      this.setSelectedItems();
      this.currentFolderId = folderId;
      this.breadcrumbs.splice(breadcrumbIndex);

      this.searchQuery = currentSearchQuery;
      await this.refreshList();
    },
    onChangePage(page) {
      this.currentPage = page;
      this.refreshList();
    },
    onSelectedItem(item) {
      if (!item || item.id === null || item.id === undefined) return;

      const isSelected = this.isSelected(item);
      if (isSelected) {
        this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id);
      } else {
        if (!this.allowMultipleSelection) {
          this.selectedItems = [];
        }
        this.selectedItems.push(item);
      }
      this.setSelectedItems(this.selectedItems);
    },
    fileDoubleClickHandler(item) {
      if (item.type === FILE_TYPE.FOLDER) return;
      if (![VIDEO_FILE_TYPES, AUDIO_FILE_TYPES, IMAGE_FILE_TYPES, PDF_FILE_TYPE, PSD_FILE_TYPE].some(types => types.includes(item.type))) {
        return;
      }
      return this.openPreview(item);
    },
    openPreview(item) {
      if (!item || item.type === FILE_TYPE.FOLDER) return;
      this.previewItem = item;
      this.$bvModal.show('select-folder-modal-preview');
    },
    async downloadItem(item) {
      if (!item || item.type === FILE_TYPE.FOLDER) return;

      try {
        let url = item.path;
        let fileName = item.name;
        const tab = this.getTab || TABS.FILES;

        if (item?.asset_type === 'bynder') {
          const { data } = await FileBrowser.downloadBynderAssets([item.asset_id]);
          url = data.url;
          fileName = data.fileName;
        } else if (item?.asset_type === 'salsify') {
          const { data } = await FileBrowser.downloadSalsifyAssets([item.id]);
          url = data.url;
          fileName = data.fileName;
        } else if (item?.asset_type === 'box') {
          const { data } = await FileBrowser.importBoxAssets([{
            id: item.id,
            name: item.name,
            extension: item.extension,
            download_url: item.download_url,
          }]);

          url = data?.files?.[0]?.url;
          fileName = item.name;
        } else if (tab === TABS.BACKGROUND || tab === TABS.STOCK_IMAGES) {
          const { data } = await FileBrowser.download({ tab, items: [item], file_ids: null });
          url = data.url;
          fileName = data.filename || data.fileName;
        } else if (tab === TABS.PRODUCTS) {
          const { data } = await FileBrowser.download({ tab, items: null, file_ids: [item.id] });
          url = data.url;
          fileName = data.filename || data.fileName;
        } else if (item.id) {
          if (tab === TABS.FILES) {
            const { data } = await FileBrowser.downloadFolderFiles([item.id]);
            url = data.url;
            fileName = data.fileName || data.name;
          } else {
            const { data } = await FileBrowser.download({ tab, items: null, file_ids: [item.id] });
            url = data.url;
            fileName = data.filename || data.name;
          }
        }

        downloadFileLink(url, fileName || 'download');
      } catch (error) {
        console.log('Download error', error?.message);
        toastr.error('Download failed. Try again later.');
      }
    },
    onFilesDragOver(event) {
      clearTimeout(this.dragLeaveTimer);
      // At the root only a folder tile can take the drop, and it lights itself up.
      this.isDraggingFiles = this.isFilesTab
        && Boolean(this.currentFolderId)
        && Boolean(event.dataTransfer?.types?.includes('Files'));
    },
    onFilesDragLeave() {
      this.dragLeaveTimer = setTimeout(() => {
        this.isDraggingFiles = false;
      }, 50);
    },
    // `DataTransfer.files` empties once the drop handler returns, so copy it before the first await.
    droppedFiles(event) {
      return event.dataTransfer?.files?.length ? Array.from(event.dataTransfer.files) : [];
    },
    onBodyDrop(event) {
      return this.onDrop(event, null);
    },
    onFolderDrop(event, folder) {
      event.preventDefault();
      event.stopPropagation();

      return this.onDrop(event, folder);
    },
    async onDrop(event, folder) {
      this.isDraggingFiles = false;

      const files = this.droppedFiles(event);

      if (!files.length) {
        return folder?.id ? this.moveDraggedItemsTo(folder, event) : undefined;
      }

      if (!this.isFilesTab) {
        return toastr.warning(NOT_FILES_TAB_MESSAGE);
      }

      // The "..." cell carries the parent id, which is null one level below the root.
      const target = folder ? folder.id : this.currentFolderId;

      if (!target) {
        return toastr.warning(NO_UPLOAD_TARGET_MESSAGE);
      }

      if (folder) {
        await this.dblHandler(folder);
      }

      return this.uploadDroppedFiles(files, target);
    },
    onItemDragStart(event, item) {
      event.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id }));
    },
    captureUploadView(folderId) {
      return {
        folderId,
        tab: this.getTab,
        search: this.searchQuery,
        favorites: this.favoritesOnly,
      };
    },
    isUploadViewActive(view) {
      // Recheck live state after every await: navigating away invalidates the snapshot.
      return !this.isDestroyed && Boolean(this.$el.closest('.modal.show'))
        && this.currentFolderId === view.folderId && this.getTab === view.tab
        && this.searchQuery === view.search && this.favoritesOnly === view.favorites;
    },
    async displayPreparedUploads(uploads, view) {
      if (!uploads.length || !this.isUploadViewActive(view)) return;

      // Filtered views need the server to decide which new files belong in the list.
      const filtered = this.isSearchActive || this.favoritesOnly || Boolean(this.rootFolderId);
      let targetPage = this.currentPage;
      if (!filtered) {
        const pagination = await this.updatePreparedUploadPagination({
          uploads,
          perPage: this.effectivePageSize,
        });
        if (!this.isUploadViewActive(view)) return;
        // Replacing a file does not append a record or require a page change.
        if (pagination.newFileCount > 0) targetPage = pagination.lastPage;
      }
      const needsList = filtered || targetPage !== this.currentPage || this.isListLoading
        || uploads.some(upload => !upload.user_file);
      if (!needsList) {
        await this.patchPreparedUploads({ uploads, append: true });
        return;
      }

      this.currentPage = targetPage;
      const dto = this.currentListDto(this.isSearchActive ? this.searchQuery : '');
      const requestedList = JSON.stringify(dto);
      dto.background = true;
      dto.preserveSelection = true;
      dto.canApply = () => this.isUploadViewActive(view)
        && requestedList === JSON.stringify(this.currentListDto(this.isSearchActive ? this.searchQuery : ''));
      await this.fetchList(dto);
      // The page must exist before resetting its tiles; transfers start after this method resolves.
      if (dto.canApply()) await this.patchPreparedUploads({ uploads });
    },
    async uploadDroppedFiles(files, folderId) {
      const view = this.captureUploadView(folderId);
      try {
        await this.uploadFilesToFolder({
          files,
          folderId,
          // Completion already patches each tile; suppress the router-based full refetch.
          onSettled: () => {},
          onPrepared: uploads => this.displayPreparedUploads(uploads, view),
        });
      } catch (error) {
        console.error('Upload failed', error);
        const message = error.response?.data?.message;
        toastr.error(typeof message === 'string' && message.trim()
          ? message : 'Upload failed. Try again later.');
      }
    },
    async moveDraggedItemsTo(folder, event) {
      if (!this.isFilesTab) return;

      const dragged = this.draggedItemsFrom(event);
      if (!dragged.length) return;

      const results = await Promise.all(
        dragged
          .filter((item) => item.id !== folder.id)
          .map((item) => this.editFile({ id: item.id, parent_id: folder.id, item, showSuccess: false }))
      );
      const moved = results.filter(Boolean).length;

      if (!moved) return;

      toastr.success(`Moved ${moved} item${moved > 1 ? 's' : ''}`);
      this.setSelectedItems();
      await this.refreshList();
    },
    draggedItemsFrom(event) {
      let payload = null;

      try {
        payload = JSON.parse(event.dataTransfer?.getData('text/plain') || 'null');
      } catch (error) {
        return [];
      }

      if (!payload?.id) return [];

      return this.selectedItems.some(({ id }) => id === payload.id)
        ? [...this.selectedItems]
        : this.items.filter(({ id }) => id === payload.id);
    },
    async createFolder() {
      try {
        this.creatingFolder = true;
        let baseName = DEFAULT_FOLDER_NAME;
        let folderName = baseName;
        let counter = 1;
        let folderExists = true;
        while (folderExists) {
          const response = await axios.post('/file/check_folder_name_exists', {
            name: folderName,
            parent_id: this.currentFolderId || null
          });

          if (response.data.exists) {
            folderName = `${baseName} (${counter})`;
            counter++;
          } else {
            folderExists = false;
          }
        }

        await axios.post('/file/create_folder_by_name', {
          name: folderName,
          parent_id: this.currentFolderId || null,
        });

        // folders sort first, so the new one is the last folder and may sit on a later page
        this.currentPage = Math.ceil((this.folderCount + 1) / this.effectivePageSize);

        await this.fetchFolderList();
        toastr.success(`Folder "${folderName}" created`);
      } catch (error) {
        console.error('Error creating folder:', error);
        toastr.error('Failed to create folder');
      } finally {
        this.creatingFolder = false;
      }
    },
    canRenameFolder(item) {
      return item && item.created_at && item.type === FILE_TYPE.FOLDER;
    },
    startRenamingFolder(item) {
      this.renamingFolderId = item.id;
      this.renamingFolderName = item.name;
      this.renamingFolderItem = item;
      this.$bvModal.show('rename-folder-modal');
    },
    async renameFolder() {
      if (!this.renamingFolderName.trim()) {
        toastr.error('Folder name cannot be empty');
        return;
      }

      const nextName = this.renamingFolderName.trim();
      const item = this.renamingFolderItem
        || this.folderItems.find((folder) => folder.id === this.renamingFolderId);

      if (isBriefLinkedFolder(item) && nextName !== item.name) {
        this.$bvModal.hide('rename-folder-modal');
        this.setModal(createModal(MODALS.BRIEF_FOLDER_RENAME, {
          id: this.renamingFolderId,
          name: nextName,
          brief_title: item.brief_title || item.name,
          brief_id: item.brief_id,
          item,
        }));
        this.renamingFolderId = null;
        this.renamingFolderName = '';
        this.renamingFolderItem = null;
        return;
      }

      try {
        await this.editFile({
          id: this.renamingFolderId,
          name: nextName,
          item: item || { type: FILE_TYPE.FOLDER },
        });
        // editFile already patches the tile name. Reloading the list replaces
        // the folders with skeleton placeholders until the request returns.
        this.$bvModal.hide('rename-folder-modal');
        this.renamingFolderId = null;
        this.renamingFolderName = '';
        this.renamingFolderItem = null;
      } catch (error) {
        console.error('Error renaming folder:', error);
      }
    },
    cancelRenameFolder() {
      this.$bvModal.hide('rename-folder-modal');
      this.renamingFolderId = null;
      this.renamingFolderName = '';
      this.renamingFolderItem = null;
    },
  }
};
</script>

<style lang="scss">
// This was inheriting `display: grid` from FolderContentView's non-scoped block, bundle permitting.
.select-folder-modal-content {
  .folders-grid {
    display: grid;
    grid-template-columns: repeat(var(--c-item-grid-columns, 6), minmax(0, 1fr));
    grid-template-rows: auto;
    justify-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 2px;
  }

  .section-container:last-child,
  .folders-grid:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 991.98px) {
    .folders-grid {
      grid-template-columns: repeat(var(--c-item-grid-columns-md, 3), minmax(0, 1fr));
    }
  }
}

.select-folder-modal-content .picker-grid-flow {
  display: contents;
}

.select-folder-modal-content.is-dragging-files .folder-scroll-area {
  outline: 2px dashed #16a38b;
  outline-offset: -4px;
  background: rgba(22, 163, 139, 0.04);
}

.select-folder-modal-content .folder-search-note {
  flex: 0 1 auto;
  margin-left: 8px;
  min-width: 0;
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.folder-drop-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 0;
  color: #8a929a;
  font-size: 13px;
}

.select-folder-preview__body {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.select-folder-preview__img {
  max-width: 100%;
  max-height: 70vh;
}

.select-folder-hover-wrap {
  position: relative;
  width: 100%;

  &:hover {
    z-index: 10;
  }
}

.select-folder-hover-actions {
  position: absolute;
  z-index: 20;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px;
  box-sizing: border-box;
  pointer-events: none;
}

.select-folder-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  pointer-events: auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;

  &--download {
    align-self: flex-start;
  }

  &--preview {
    align-self: flex-end;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.65);
    border-color: #fff;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(22, 163, 139, 0.35);
  }
}
</style>
